import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  DIALOG_DATA,
  getFormattedDate,
  getFormattedDateTime,
  getFormattedTime,
  MAX_SHIFT_DURATION,
} from '../../../../global.variable';
import { ClientsService } from '../../../pages/pages/clients/clients.service';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { DialogRef } from '../modal-dialogue/modal-dialogue.ref';
import { ModalDialogueService } from '../modal-dialogue/modal-dialogue.service';

@Component({
  selector: 'gtapp-change-schedule',
  standalone: false,
  templateUrl: './change-schedule.component.html',
  styleUrl: './change-schedule.component.scss',
})
export class ChangeScheduleComponent implements OnInit {
  hasJobStarted: boolean = false;
  disableShiftUpdate: boolean = false;
  startDateTime: any;
  validStartDate: any;
  endDateTime: any;
  validEndDate: any;

  schedulerForm: UntypedFormGroup = new UntypedFormGroup({
    end_time: new FormControl(null),
    start_time: new FormControl(null),
    start_day: new FormControl(new Date()),
    end_day: new FormControl(null, Validators.required),

    repeat_times: new UntypedFormControl(null),
    repeat_type: new UntypedFormControl(null, Validators.required),
    repeat_details: new UntypedFormControl([]),
    repeat_interval: new UntypedFormControl(1, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
    ]),
    position: new UntypedFormControl(null, []),
    week_day: new UntypedFormControl(null, []),
    month_repeat_case: new UntypedFormControl(null, []),
  });

  errorMessage: string = '';

  initialDateTimeValues: any = {};
  constructor(
    @Optional() protected dialogRef: DialogRef,
    private clientService: ClientsService,
    @Inject(DIALOG_DATA) public dialogueData: any,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,
    private dialogService: ModalDialogueService,
  ) {}
  ngOnInit() {
    if (!this.dialogueData?.roster_scheduler) {
      this.onCloseDialogue();
      return;
    }
    this.schedulerForm.patchValue(this.dialogueData?.roster_scheduler);
    this.validStartDate = getFormattedDateTime();
    const scheduleEventDate = this.dialogueData?.schedule_event_date;
    const startDateTime = new Date(
      (scheduleEventDate || this.schedulerForm.value.start_day) +
        ' ' +
        this.schedulerForm.value.start_time,
    );
    this.hasJobStarted = new Date().getTime() > startDateTime.getTime();

    const endDateTime = new Date(
      (scheduleEventDate || this.schedulerForm.value.end_day) +
        ' ' +
        this.schedulerForm.value.end_time,
    );
    if (
      this.schedulerForm?.value?.start_time >
        this.schedulerForm?.value?.end_time &&
      this.schedulerForm?.value?.repeat_type != 'once'
    ) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    this.endDateTime = getFormattedDateTime(endDateTime);
    this.startDateTime = getFormattedDateTime(startDateTime);
    this.initialDateTimeValues = {
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
    };
    this.schedulerForm.controls['start_day'].setValue(
      getFormattedDate(startDateTime),
    );
    this.schedulerForm.controls['start_time'].setValue(
      getFormattedTime(startDateTime),
    );
    this.schedulerForm.controls['end_day'].setValue(
      getFormattedDate(endDateTime),
    );
    this.schedulerForm.controls['end_time'].setValue(
      getFormattedTime(endDateTime),
    );
    this.schedulerForm.controls['repeat_type'].setValue('once');
    this.validationCheck();
  }
  onCloseDialogue(val = false) {
    this.dialogRef.close(val);
  }
  validationCheck() {
    const startDateTime = new Date(this.startDateTime)?.getTime();
    const endDateTime = new Date(this.endDateTime)?.getTime();
    const shiftTimeExceeded =
      endDateTime - startDateTime > MAX_SHIFT_DURATION * 60 * 60 * 1000;
    this.disableShiftUpdate = false;
    this.errorMessage = '';
    if (shiftTimeExceeded) {
      this.disableShiftUpdate = true;
      this.errorMessage = 'Shift Time Exceeded.';
      this.schedulerForm.controls['end_day'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_time'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_day'].setErrors(null);
    }

    const isStartTimeGreater = startDateTime > endDateTime;

    if (isStartTimeGreater) {
      this.disableShiftUpdate = true;
      this.errorMessage = 'End time must be greater than start time.';
      this.schedulerForm.controls['end_day'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_time'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_day'].setErrors(null);
    }
    const isSame =
      new Date(this.initialDateTimeValues?.startDateTime)?.getTime() ==
        startDateTime &&
      new Date(this.initialDateTimeValues?.endDateTime)?.getTime() ==
        endDateTime;

    if (isSame) {
      this.disableShiftUpdate = true;

      this.schedulerForm.controls['end_day'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_time'].setErrors({
        invalidStartDate: true,
      });
      this.schedulerForm.controls['end_day'].setErrors(null);
    }
  }
  changeShiftDateTime(dateTimeType: number, event: any) {
    const selectedDate = new Date(event.target.value);
    if (dateTimeType === 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        this.schedulerForm.controls['start_day'].setErrors({
          invalidStartDate: true,
        });
        this.errorMessage = 'Start time must be greater than today.';
        this.disableShiftUpdate = true;
        return;
      }
      this.schedulerForm.controls['start_day'].setValue(
        getFormattedDate(event.target.value),
      );
      this.schedulerForm.controls['start_time'].setValue(
        getFormattedTime(event.target.value),
      );
      const scheduleEventDate = this.dialogueData?.schedule_event_date;
      const scheduleEndDateTime = new Date(
        (scheduleEventDate || this.schedulerForm.value.end_day) +
          ' ' +
          this.schedulerForm.value.end_time,
      );

      if (
        scheduleEndDateTime.getTime() < new Date(event.target.value).getTime()
      ) {
        const newEndDate = new Date(event.target.value);
        newEndDate.setHours(newEndDate.getHours() + 8);

        this.schedulerForm.controls['end_day'].setValue(
          getFormattedDate(newEndDate),
        );
        this.schedulerForm.controls['end_time'].setValue(
          getFormattedTime(newEndDate),
        );
        this.endDateTime = getFormattedDateTime(newEndDate);
      }
    } else {
      this.schedulerForm.controls['end_day'].setValue(
        getFormattedDate(event.target.value),
      );

      this.schedulerForm.controls['end_time'].setValue(
        getFormattedTime(event.target.value),
      );
    }

    this.validationCheck();
  }
}
